/**
 * Slice LV-01 · LeviVoiceIntake
 * =============================
 *
 * Mobile-first voice-intake surface, mounted inside the shared
 * <LeviAssistantDrawer>. Renders exactly one primary control at a time
 * so the contractor is never confused about the current interaction
 * state:
 *
 *   idle / ready  → giant "Tap to speak" mic button
 *   listening     → red pulsing "Stop" button + live indicator
 *   uploading /
 *   thinking      → Levi-is-working chip
 *   awaiting_
 *     confirmation → Yes / Correct / Cancel buttons
 *   creating      → single "Creating your records…" chip
 *   finished      → three big next-step buttons
 *
 * Voice-first: text input is a small collapsible fallback, never
 * presented as the primary path.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Mic, MicOff, StopCircle, Loader2, CheckCircle2, AlertTriangle,
  Sparkles, Video, ClipboardList, ArrowRight, Keyboard,
} from "lucide-react";
import { useLeviVoiceIntake } from "@/hooks/useLeviVoiceIntake";
import { speakLeviReply, stopSpeaking } from "@/lib/leviSpeech";
import { useLang } from "@/context/LangContext";

// LEVI-DOC-PREP-01 · Defect C · Truthful microphone-state copy.
// `ready` means the intake session has been created but the
// MediaRecorder is NOT capturing. The previous "Levi is listening…"
// wording was a false claim about active microphone capture. The
// only state where the mic is actually capturing is `listening`
// (see useLeviVoiceIntake.js:96-124 where mr.start(CHUNK_MS) is
// invoked). Copy for every other state MUST NOT imply active
// capture.
const stateLabel = {
  idle: "Levi is starting…",
  booting: "Levi is starting…",
  ready: "Tap to speak when you're ready.",
  requesting_mic: "Requesting your microphone…",
  listening: "Listening… speak naturally.",
  uploading: "Sending what you said to Levi…",
  thinking: "Levi is working on that…",
  awaiting_confirmation: "Levi is ready to create these records.",
  creating: "Creating your customer, project, and draft estimate…",
  finished: "All set.",
  error: "Something went wrong.",
};

function IntakeSummary({ intake }) {
  const c = intake?.customer || {};
  const sc = intake?.secondary_contact || {};
  const a = intake?.address || {};
  const trade = intake?.trade || "";
  const ptype = intake?.project_type || "";
  const measurements = intake?.measurements || [];
  const upgrades = intake?.upgrades || [];
  const materials = intake?.materials || [];
  return (
    <div className="rounded-lg border border-[var(--jc-border)] bg-[var(--jc-surface)] p-3 space-y-2 text-sm" data-testid="levi-intake-summary-card">
      <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
        What Levi has so far
      </div>
      {c.name && <div><span className="text-[var(--jc-text-soft)]">Customer:</span> <span className="font-medium">{c.name}</span> {c.phone && <span className="ml-1 text-[var(--jc-text-soft)]">· {c.phone}</span>} {c.email && <span className="ml-1 text-[var(--jc-text-soft)]">· {c.email}</span>}</div>}
      {sc.name && <div><span className="text-[var(--jc-text-soft)]">Also on the project:</span> <span className="font-medium">{sc.name}</span> {sc.relationship && <span className="text-[var(--jc-text-soft)]"> ({sc.relationship})</span>}</div>}
      {(a.street || a.city || a.state) && (
        <div><span className="text-[var(--jc-text-soft)]">Address:</span> {[a.street, a.city, `${a.state || ""} ${a.zip || ""}`.trim()].filter(Boolean).join(", ")}</div>
      )}
      {(trade || ptype) && (
        <div><span className="text-[var(--jc-text-soft)]">Trade:</span> <span className="font-medium">{trade || "—"}</span>{ptype && <span className="ml-2 text-[var(--jc-text-soft)]">· {ptype}</span>}</div>
      )}
      {measurements.length > 0 && (
        <div>
          <span className="text-[var(--jc-text-soft)]">Measurements:</span>
          <ul className="ml-3 mt-1 space-y-0.5">
            {measurements.map((m, i) => (
              <li key={i}>• {m.label}: {m.value} {m.unit}</li>
            ))}
          </ul>
        </div>
      )}
      {upgrades.length > 0 && (
        <div><span className="text-[var(--jc-text-soft)]">Upgrades:</span> {upgrades.join(", ")}</div>
      )}
      {materials.length > 0 && (
        <div><span className="text-[var(--jc-text-soft)]">Materials:</span> {materials.join(", ")}</div>
      )}
    </div>
  );
}

function TranscriptStrip({ turns }) {
  const [open, setOpen] = useState(false);
  const shown = (turns || []).slice(-6);
  if (shown.length === 0) return null;
  return (
    <details className="rounded-md border border-[var(--jc-border)]" onToggle={(e) => setOpen(e.currentTarget.open)} data-testid="levi-intake-transcript-details">
      <summary className="p-2 text-xs text-[var(--jc-text-soft)] cursor-pointer">
        {open ? "Hide" : "View"} the conversation
      </summary>
      <div className="p-2 space-y-2 text-xs max-h-56 overflow-y-auto">
        {shown.map((t, i) => (
          <div key={i} className={t.role === "levi" ? "text-[var(--jc-text)]" : "text-[var(--jc-text-soft)]"}>
            <span className="font-semibold uppercase mr-2 opacity-60">{t.role}</span>
            {t.text}
          </div>
        ))}
      </div>
    </details>
  );
}

function TextFallback({ onSubmit, disabled }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-[var(--jc-text-soft)] underline inline-flex items-center gap-1"
        data-testid="levi-intake-text-fallback-toggle"
      >
        <Keyboard size={12} /> Type instead
      </button>
    );
  }
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) {
          onSubmit(text.trim());
          setText("");
        }
      }}
    >
      <textarea
        rows={3}
        className="w-full rounded-md border border-[var(--jc-border)] p-2 text-sm"
        placeholder="Type what you would have said to Levi…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="levi-intake-text-fallback-input"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={disabled || !text.trim()}
          className="rounded-md bg-black text-white text-xs px-3 py-1.5 font-semibold disabled:opacity-40"
          data-testid="levi-intake-text-fallback-submit"
        >
          Send
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setText(""); }}
          className="text-xs text-[var(--jc-text-soft)] underline"
        >
          Close
        </button>
      </div>
    </form>
  );
}

export default function LeviVoiceIntake({ onClose, hideOwnHeader = false, onIntakeCompleted, onIntakeCancelled, initialContext = null }) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const {
    session, status, error, micDenied, finalizing,
    startListening, stopAndSend, cancelListening, submitText, finalize,
  } = useLeviVoiceIntake({ enabled: true, initialContext });

  const intake = session?.structured_intake;
  const pendingQ = session?.pending_question;
  const latestLevi = useMemo(() => {
    const turns = session?.turns || [];
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "levi") return turns[i].text;
    }
    return "";
  }, [session]);

  // LEVI-BEH-01 correction 3 · Completion handoff. When the intake
  // session finalizes (existing behavior — customer + project +
  // draft estimate are created via the intake pipeline the same way
  // they always have been), we notify the parent unified console so
  // it can append a meaningful unified transcript turn continuing
  // the same conversation. Fires exactly once per finalized session.
  const completedHandledRef = useRef(null);
  useEffect(() => {
    if (session?.status !== "finalized") return;
    const key = session?.id || "finalized";
    if (completedHandledRef.current === key) return;
    completedHandledRef.current = key;
    onIntakeCompleted?.(session);
  }, [session, onIntakeCompleted]);

  // Slice LV-02 (spoken-reply handoff) — LV-01 previously rendered
  // Levi's replies as text only. To satisfy the LV-02 conversational
  // speech acceptance criteria without touching LV-01's locked
  // business logic we add ONLY a one-way handoff: whenever a NEW Levi
  // turn is committed to the visible transcript AND the contractor
  // isn't currently speaking, we forward the exact visible reply text
  // to the shared `speakLeviReply()` pipeline. No estimate, intake,
  // finalization, or transcript logic is modified.
  const lastSpokenLeviRef = useRef("");
  useEffect(() => {
    const text = (latestLevi || "").trim();
    if (!text) return;
    if (text === lastSpokenLeviRef.current) return;   // already spoken
    if (status === "listening" || status === "uploading") return; // don't overtalk the mic
    lastSpokenLeviRef.current = text;
    speakLeviReply(text, lang || "en", {
      source: "normal_reply",
      mode: "voice_intake",
      status,
      isListening: status === "listening",
    });
  }, [latestLevi, status, lang]);
  // Interrupt Levi's speech the moment the mic starts.
  useEffect(() => {
    if (status === "listening") stopSpeaking("lv01:status:listening");
  }, [status]);

  const finishedProject = session?.status === "finalized" ? session?.created_project_id : null;

  // ------------------------------------------------------------------
  // Voice command router for the success screen ("Start the video job
  // walk.", "Continue the estimate.", "Let me review the customer
  // information."). We intercept text answers when finished + not
  // navigated yet. Voice-based intent is done by re-using the same
  // /turn endpoint would over-engineer for VW-01-adjacent scope, so
  // for LV-01 we route the three buttons through both tap and a small
  // client-side keyword matcher fed by transcripts.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!finishedProject) return;
    const t = (latestLevi || "").toLowerCase();
    if (!t) return;
    // No auto-nav — the contractor explicitly selects the next step so
    // we never make a surprising jump.
  }, [finishedProject, latestLevi]);

  const goStartVideoWalk = () => {
    if (!finishedProject) return;
    onClose?.();
    navigate(`/app/projects/${finishedProject}?intent=start-video-walk`);
  };
  const goContinueEstimate = () => {
    if (!session?.created_estimate_id) return;
    onClose?.();
    navigate(`/app/estimates/${session.created_estimate_id}`);
  };
  const goReview = () => {
    if (!finishedProject) return;
    onClose?.();
    navigate(`/app/projects/${finishedProject}`);
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  return (
    <div className="space-y-4" data-testid="levi-voice-intake">
      {!hideOwnHeader && (
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2 shrink-0">
            <Sparkles size={16} className="text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
              Levi — Voice-First New Estimate
            </div>
            <div className="text-base font-semibold mt-0.5" data-testid="levi-intake-greeting">
              {session?.greeting || "Welcome. How can I help you today?"}
            </div>
            {latestLevi && latestLevi !== session?.greeting && (
              <p className="text-sm text-[var(--jc-text)] mt-2 whitespace-pre-wrap" data-testid="levi-intake-latest-line">
                {latestLevi}
              </p>
            )}
            {pendingQ && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900" data-testid="levi-intake-pending-question">
                {pendingQ}
              </div>
            )}
          </div>
        </div>
      )}
      {hideOwnHeader && (
        <div className="flex items-start gap-2" data-testid="levi-intake-inline-header">
          <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
            {lang === "es" ? "Nuevo estimado" : "New estimate"}
          </div>
          {latestLevi && (
            <p className="text-sm text-[var(--jc-text)] flex-1 whitespace-pre-wrap" data-testid="levi-intake-latest-line">
              {latestLevi}
            </p>
          )}
        </div>
      )}
      {hideOwnHeader && pendingQ && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900" data-testid="levi-intake-pending-question">
          {pendingQ}
        </div>
      )}

      <div className="text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-2" data-testid="levi-intake-status">
        {(status === "uploading" || status === "thinking" || status === "creating" || status === "booting" || status === "requesting_mic") && (
          <Loader2 size={12} className="animate-spin" />
        )}
        {status === "listening" && (
          <span className="inline-flex items-center gap-1 text-red-700">
            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
            {stateLabel[status]}
          </span>
        )}
        {status !== "listening" && stateLabel[status]}
      </div>

      {/* Primary controls — exactly one at a time */}
      {session?.status !== "finalized" && (
        <div className="space-y-3">
          {status === "ready" && (
            <button
              type="button"
              onClick={startListening}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-4 py-3 text-base font-semibold hover:bg-red-700"
              data-testid="levi-intake-mic-start-btn"
            >
              <Mic size={18} /> Tap to speak to Levi
            </button>
          )}
          {status === "listening" && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={stopAndSend}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-black text-white px-4 py-3 text-base font-semibold"
                data-testid="levi-intake-mic-stop-btn"
              >
                <StopCircle size={18} /> Send what I just said
              </button>
              <button
                type="button"
                onClick={cancelListening}
                className="text-xs text-[var(--jc-text-soft)] underline"
                data-testid="levi-intake-mic-cancel-btn"
              >
                Discard this recording
              </button>
            </div>
          )}
          {session?.status === "awaiting_confirmation" && !finalizing && (
            <div className="flex flex-col gap-2" data-testid="levi-intake-confirmation-controls">
              <button
                type="button"
                onClick={finalize}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-black text-white px-4 py-3 text-base font-semibold"
                data-testid="levi-intake-confirm-btn"
              >
                <CheckCircle2 size={18} /> Yes, create everything
              </button>
              <button
                type="button"
                onClick={startListening}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm"
                data-testid="levi-intake-correct-btn"
              >
                <Mic size={16} /> Speak a correction
              </button>
            </div>
          )}
        </div>
      )}

      {/* LEVI-BEH-01 correction 3 · Inline close. When the intake
          is mounted inline inside the unified console, the
          contractor can back out without a whole-drawer swap. We
          only append a unified transcript turn when the intake had
          actually captured a fact (otherwise it's noise). */}
      {hideOwnHeader && session?.status !== "finalized" && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => {
              const c = intake?.customer || {};
              const a = intake?.address || {};
              const hadFacts = Boolean(
                (c.name && c.name.trim())
                || (c.phone && c.phone.trim())
                || (c.email && c.email.trim())
                || (a.street && a.street.trim())
              );
              onIntakeCancelled?.({ hadFacts });
            }}
            className="text-xs text-[var(--jc-text-soft)] underline"
            data-testid="levi-intake-inline-close"
          >
            {lang === "es"
              ? "Volver a la conversación con Levi"
              : "Back to Levi"}
          </button>
        </div>
      )}

      {/* Structured intake summary */}
      {intake && (
        (intake.customer?.name || intake.address?.street || intake.trade) && (
          <IntakeSummary intake={intake} />
        )
      )}

      {/* Session transcript, collapsed */}
      <TranscriptStrip turns={session?.turns} />

      {/* Text fallback — never the primary path */}
      {session && session.status !== "finalized" && (
        <TextFallback
          onSubmit={submitText}
          disabled={status === "listening" || status === "uploading" || status === "thinking"}
        />
      )}

      {/* Errors */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-2 inline-flex items-start gap-2" data-testid="levi-intake-error">
          <AlertTriangle size={14} className="mt-0.5" />
          <div>{error}</div>
        </div>
      )}
      {micDenied && (
        <div className="text-xs text-[var(--jc-text-soft)]" data-testid="levi-intake-mic-denied-hint">
          Microphone permission is required for voice intake. You can also
          type your response with "Type instead" below.
        </div>
      )}

      {/* Success screen — three primary next steps */}
      {session?.status === "finalized" && (
        <div className="space-y-4" data-testid="levi-intake-success">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <div className="font-semibold inline-flex items-center gap-2">
              <CheckCircle2 size={14} /> Your {intake?.trade || ""} estimate has been started.
            </div>
            {session?.pricing_ready === false && (
              <p className="mt-2" data-testid="levi-intake-pricing-message">
                {session.pricing_message}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={goStartVideoWalk}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-4 py-3 text-base font-semibold"
              data-testid="levi-intake-next-video-walk-btn"
            >
              <Video size={18} /> Start Video Job Walk <ArrowRight size={14} />
            </button>
            <button
              type="button"
              onClick={goContinueEstimate}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-base"
              data-testid="levi-intake-next-estimate-btn"
            >
              <ClipboardList size={18} /> Continue Estimate
            </button>
            <button
              type="button"
              onClick={goReview}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-base"
              data-testid="levi-intake-next-review-btn"
            >
              <Sparkles size={18} /> Review Captured Information
            </button>
          </div>
          {session?.pricing_ready === false && (
            <div className="text-xs text-[var(--jc-text-soft)]">
              Missing pricing? <Link to="/app/pricing-profile" className="underline" onClick={onClose}>Open pricing setup</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
